import { useMergeRefs } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Center,
  Checkbox,
  failedChildrenLanguage,
  failedPartLanguage,
  Flex,
  Text,
  Tooltip,
  CaretDownIcon,
  WarningIcon,
} from '@frontend/components';
import {
  TemporarilySimplifiedIndicatorForBody,
  TooComplexIndicatorForBody,
} from '@frontend/partviewer';

import FileTypeIcon from 'components/lib2/fileTypeIcon/FileTypeIcon';

import {
  cadViewer,
  viewerInteractions,
} from 'modules/common/viewer/cadViewer/data/actions';
import {
  getIsBodyFocused,
  getIsInteractionReady,
  getTemporarilySimplified,
  getTooComplexForPartViewer,
} from 'modules/common/viewer/cadViewer/data/reducer';

import { useHover, useLongPress } from 'utils/hooks';

import { GEOMETRIC_SUPPORTING_FILES } from './';
import type { CadNode, CadPopoverNodeData, CadPopoverPosition } from './';

enum CheckedState {
  Unchecked,
  Indeterminate,
  Checked,
}

// Expand / Collapse icon
const Icon = ({
  handleClick,
  isExpanded,
}: {
  handleClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  isExpanded: boolean;
}) => {
  return (
    <>
      <Flex
        h={3}
        w={3}
        justify="center"
        align="center"
        cursor="pointer"
        {...(handleClick && { onClick: handleClick })}
      >
        <CaretDownIcon
          size={1.5}
          transform={isExpanded ? null : 'rotate(270deg)'}
        />
      </Flex>
    </>
  );
};

export type TreeNodeProps = {
  node: CadNode;
  isExpanded: boolean;
  toggleNodeExpanded: () => boolean;
  depth: number;
  selectedKeys: Set<string>;
  checkedKeys: Set<string>;
  focusedKeys: Set<string>;
  handleCheck: (node: CadNode, checked: boolean) => void;
  handleSelect: (bodyId: string, remove: boolean) => void;
  handleRightClick: (
    e: React.MouseEvent<HTMLDivElement> | null,
    popoverNodeData: CadPopoverNodeData,
  ) => void;
  handleThumbnailHover: (popoverNodeData: CadPopoverNodeData | null) => void;
};

export const TreeNode = ({
  node,
  isExpanded,
  toggleNodeExpanded,
  depth = 0,
  selectedKeys,
  checkedKeys,
  focusedKeys,
  handleCheck,
  handleSelect,
  handleRightClick,
  handleThumbnailHover,
}: TreeNodeProps) => {
  const {
    viewerId: bodyId,
    partFileUuid,
    fileType,
    title,
    isParentFocused,
    processingFailed,
    hasFailedChildren,
    isSupportingFile,
    children,
  } = node;

  const isSupportingFileRoot = bodyId === GEOMETRIC_SUPPORTING_FILES;

  const dispatch = useDispatch();
  const isInteractionReady = useSelector(getIsInteractionReady);
  const isFocused = useSelector((state) => getIsBodyFocused(state, bodyId));
  const positionRef = useRef<HTMLDivElement | null>(null);

  const temporarilySimplifiedBody = useSelector(getTemporarilySimplified);
  const tooComplexForPartViewer = useSelector(getTooComplexForPartViewer);

  const isTemporarilySimplified: boolean = useMemo(
    () => temporarilySimplifiedBody?.has(bodyId),
    [bodyId, temporarilySimplifiedBody],
  );
  const isTooComplexForPartViewer: boolean = useMemo(
    () => tooComplexForPartViewer?.has(partFileUuid),
    [partFileUuid, tooComplexForPartViewer],
  );

  // checkedState is 'unchecked', 'indeterminate', 'checked' of Checkbox
  // isNodeOrChildFocused is used to track if you or any of your children is the "focused" node/body, so you can stay in the "active" state in the CAD tree
  const [checkedState, isNodeOrChildFocused]: [CheckedState, boolean] =
    useMemo(() => {
      // Check if node is focused
      let isInFocus = focusedKeys.has(bodyId);

      // If last descendent
      if (!children.length) {
        if (checkedKeys.has(bodyId)) {
          return [CheckedState.Checked, isInFocus];
        } else {
          return [CheckedState.Unchecked, isInFocus];
        }
      }

      let someChecked = false;
      let someUnchecked = false;

      const stack = [...children];
      let child: CadNode | undefined;

      // Only count last descendents as middle nodes may not be in the checkedKeys array
      while ((child = stack.pop())) {
        if (child.children?.length) {
          for (const nextChild of child.children) {
            stack.push(nextChild);
          }
        } else {
          if (checkedKeys.has(child.viewerId)) {
            someChecked = true;
          } else {
            someUnchecked = true;
          }
        }
        // Check if any child of node is focused
        if (focusedKeys.has(child.viewerId)) {
          isInFocus = true;
        }
      }

      return [
        someChecked
          ? someUnchecked
            ? CheckedState.Indeterminate
            : CheckedState.Checked
          : CheckedState.Unchecked,
        isInFocus,
      ];
    }, [children, checkedKeys, focusedKeys, bodyId]);

  // selectState captures the "selected" state of the CAD Tree, that represents "highlight" in the Viewer
  const selectedState = useMemo(() => {
    if (selectedKeys.has(bodyId)) {
      return true;
    } else {
      return false;
    }
  }, [selectedKeys, bodyId]);

  // temporarily issues a "highlight" call to the Viewer to highlight the hovered node's body
  const onHover = useCallback(
    (hovered: boolean, exitTriggered: boolean) => {
      if (!isInteractionReady) return;
      if (hovered) {
        dispatch(cadViewer.receiveHoverBody({ hoverBody: bodyId }));
      } else if (!hovered && exitTriggered) {
        dispatch(cadViewer.receiveHoverBody({ hoverBody: null }));
      }
    },
    [dispatch, isInteractionReady, bodyId],
  );

  // double click acts as a call to focus the node / body
  // single click selects / highlights the node / body
  const handleClick = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // double click
      if (e.detail === 2) {
        if (!isInteractionReady) return;
        dispatch(cadViewer.receiveHoverBody({ hoverBody: null }));
        dispatch(viewerInteractions.focusOnBodies({ viewerBodyIds: [bodyId] }));
      } else {
        handleSelect(bodyId, selectedState);
      }
    },
    [bodyId, selectedState, isInteractionReady, handleSelect, dispatch],
  );

  // long press used to mimic double click for touch events
  const longPressAction = useLongPress(((popoverPosition: CadPopoverPosition) =>
    handleRightClick(null, { node, popoverPosition })) as () => void);

  const [hoverRef, hovered, exitTriggered] = useHover(300) as [
    React.MutableRefObject<HTMLDivElement>,
    boolean,
    boolean,
  ];
  const mergedRefs = useMergeRefs(positionRef, hoverRef);

  useEffect(() => {
    onHover(hovered, exitTriggered);
  }, [hovered, exitTriggered, onHover]);

  // Display the section header for the "Geometric Supporting Files" trees, if available
  if (isSupportingFileRoot) {
    return (
      <Flex
        mb={0.5}
        bg="page.plateMedium"
        align="center"
        cursor="pointer"
        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
          toggleNodeExpanded();
          e.stopPropagation();
        }}
      >
        <Icon isExpanded={isExpanded} />
        <Flex
          as={'button'}
          h={3}
          gap={0.5}
          align="center"
          outline="none"
          textAlign="left"
          type="button"
        >
          <Text
            ml="3px"
            fontWeight={600}
            fontSize="14px"
            lineHeight="24px"
            userSelect="none"
          >
            {title}
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <>
      <Flex
        pl={(isSupportingFile ? (depth > 0 ? depth - 1 : 0) : depth) * 3}
        mb={0.5}
        align="center"
      >
        {children?.length ? (
          <Icon
            isExpanded={isExpanded}
            handleClick={(e: React.MouseEvent<HTMLDivElement>) => {
              toggleNodeExpanded();
              e.stopPropagation();
            }}
          />
        ) : (
          <Box w={3} />
        )}
        <Flex
          minHeight={3}
          gap={0.5}
          align="center"
          cursor="pointer"
          outline="none"
          textAlign="left"
        >
          <Checkbox
            isChecked={checkedState === CheckedState.Checked}
            isIndeterminate={checkedState === CheckedState.Indeterminate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleCheck(node, e.target.checked);
            }}
          />
          <Flex
            ref={mergedRefs}
            align="center"
            p="2px 4px"
            maxW={30}
            bg={selectedState ? '#bae7ff' : 'none'}
            _hover={{
              bg: selectedState ? '#bae7ff' : '#ecf6fd',
            }}
            onClick={handleClick}
            // onContextMenu trigger the popover of the context menu (handled in CadTree component)
            // positional data included to place the popover next to this trigger
            onContextMenu={(e) => {
              const rect = positionRef?.current?.getBoundingClientRect();
              handleRightClick(e, {
                node,
                popoverPosition: {
                  top: rect?.top ?? 0,
                  left: (rect?.left ?? 0) + (rect?.width ?? 0) + 2,
                },
              });
            }}
            onTouchStart={longPressAction.onTouchStart}
            onTouchEnd={longPressAction.onTouchEnd}
          >
            {processingFailed || hasFailedChildren ? (
              <Tooltip
                label={
                  <>
                    <Text fontWeight="bold" color="feature.onInteraction">
                      {processingFailed
                        ? failedPartLanguage.headline
                        : failedChildrenLanguage.headline}
                    </Text>
                    <Text color="feature.onInteraction" variant="expanded">
                      {processingFailed
                        ? failedPartLanguage.description
                        : failedChildrenLanguage.description}
                    </Text>
                  </>
                }
              >
                <Center w={2.5} color="alert.error">
                  <WarningIcon />
                </Center>
              </Tooltip>
            ) : (
              <FileTypeIcon
                size={2.5}
                color={
                  isNodeOrChildFocused || isParentFocused
                    ? 'feature.secondary'
                    : 'rgba(0, 0, 0, 0.25)'
                }
                fileType={fileType}
                // onMouseEnter / onMouseLeave trigger the popover of the part Thumbnail (handled in CadTree component)
                // positional data included to place the popover next to this trigger
                onMouseEnter={() => {
                  const rect = positionRef?.current?.getBoundingClientRect();
                  handleThumbnailHover({
                    node,
                    popoverPosition: {
                      top: rect?.top ?? 0,
                      left: rect?.left ?? 0,
                    },
                  });
                }}
                onMouseLeave={() => {
                  handleThumbnailHover(null);
                }}
              />
            )}
            <Text
              color={
                isNodeOrChildFocused || isParentFocused
                  ? 'feature.primary'
                  : 'rgba(0, 0, 0, 0.25)'
              }
              ml="3px"
              fontWeight={isFocused ? 700 : 400}
              wordBreak="break-all"
              fontSize="12px"
              lineHeight="20px"
              fontFamily="Roboto Mono"
              userSelect="none"
            >
              {title}
            </Text>
          </Flex>
        </Flex>
        {isTemporarilySimplified ? (
          <Box key="temporarilySimplified" alignSelf="center" ml="auto" pr={1}>
            <TemporarilySimplifiedIndicatorForBody boxSize={3} />
          </Box>
        ) : isTooComplexForPartViewer ? (
          <Box key="tooComplex" alignSelf="center" ml="auto" pr={1}>
            <TooComplexIndicatorForBody boxSize={3} />
          </Box>
        ) : null}
      </Flex>
    </>
  );
};
